import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {makeMapDispatchToProps, makeMapStateToProps} from '../../../helpers/reselectHelpers';
import {AccountModel} from '../../../models/api/accountModel';
import {AppState} from '../../../store/appState';
import {ThunkAction, ThunkAsyncAction} from '../../../store/appThunk';
import {CreateAccountOptions} from '../../../store/data/accounts/accountsActions';
import {getCards} from '../../../store/data/dataSelectors';
import {
  ContactDetailsOrContactsManagerPanelId,
  createAccountFormCreateAccountAction,
} from '../../../store/panels/accounts/createAccountForm/createAccountFormActions';
import {
  contactDetailsOpenAccountDetailsAction,
  contactDetailsOpenContactDetailsAction,
} from '../../../store/panels/contacts/contactDetailsPanelActions';
import {
  ContactDetailsCreateAccountSourceActionsEnum,
  ContactDetailsPanelCreateAccountSubState,
  ContactDetailsSubStateTypesEnum,
} from '../../../store/panels/contacts/contactDetailsPanelSubState';
import {ContactsManagerCreateAccountFormState} from '../../../store/panels/contacts/contactsManager/sidePanel/contactsManagerCreateAccountFormState';
import {closeSidePanelAction} from '../../../store/panels/contacts/contactsManager/sidePanel/contactsManagerSidePanelActions';
import {ContactsManagerSidePanelTypesEnum} from '../../../store/panels/contacts/contactsManager/sidePanel/contactsManagerSidePanelStateBase';
import {ContactDetailsPanelId} from '../../../store/panels/panelStateBase';
import {navigateToLinkAction} from '../../../store/stateless/navigationActions';
import {CreateAccountFooter} from './createAccountFooter';

interface CreateAccountFooterContainerProps {
  panelId: ContactDetailsOrContactsManagerPanelId;
  createAccountFormState: ContactsManagerCreateAccountFormState | ContactDetailsPanelCreateAccountSubState;
}

/*
 * Selectors.
 */

function getCreateAccountFormState(_state: AppState, props: CreateAccountFooterContainerProps) {
  return props.createAccountFormState;
}

function makeGetIsEditingAccountOnCard() {
  return createSelector([getCreateAccountFormState, getCards], (formState, cards) => {
    // If we are in the contacts manager, we can ignore this check.
    if (formState.type === ContactsManagerSidePanelTypesEnum.CREATE_ACCOUNT) {
      return false;
    }

    const card = cards[formState.cardId];
    if (!card) {
      return false;
    }
    return Boolean(card.accountId);
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CreateAccountFooterContainerProps>()(() => ({
  isEditingAccountOnCard: makeGetIsEditingAccountOnCard(),
}));

const mapDispatchToProps = makeMapDispatchToProps<CreateAccountFooterContainerProps>()((props) => ({
  createAccount: (
    createOptions: CreateAccountOptions,
    avatarFile: File | undefined,
  ): ThunkAsyncAction<AccountModel | undefined> =>
    createAccountFormCreateAccountAction(props.panelId, createOptions, avatarFile),
  closeSidePanel:
    (newAccountId?: number): ThunkAction =>
    (dispatch) => {
      if (props.createAccountFormState.type === ContactsManagerSidePanelTypesEnum.CREATE_ACCOUNT) {
        dispatch(closeSidePanelAction());
      }
      if (props.createAccountFormState.type === ContactDetailsSubStateTypesEnum.CREATE_ACCOUNT) {
        if (
          props.createAccountFormState.source ===
            ContactDetailsCreateAccountSourceActionsEnum.ACCOUNT_DETAILS ||
          newAccountId
        ) {
          dispatch(
            contactDetailsOpenAccountDetailsAction(props.panelId as ContactDetailsPanelId, newAccountId),
          );
        } else {
          dispatch(contactDetailsOpenContactDetailsAction(props.panelId as ContactDetailsPanelId));
        }
      }
    },
  navigateToLink: navigateToLinkAction,
}));

export const CreateAccountFooterContainer = connect(mapStateToProps, mapDispatchToProps)(CreateAccountFooter);
